import { Box, Flex, Text } from '@chakra-ui/react'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js'
import { useRef, useMemo } from 'react'
import { Line } from 'react-chartjs-2'

import { SectionCard } from '../../../../components/SectionCard'
import { formatCurrencyValue } from '../../../../helpers'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler)

interface IMetricCardProps {
  icon: string
  title: string
  indicator: string
  value: number
  previousValue: number | null
  color: string
  sparklineData: number[]
}

export default function MetricCard({
  icon,
  title,
  indicator,
  value,
  previousValue,
  color,
  sparklineData
}: IMetricCardProps) {
  const formattedValue = indicator === '%' ? `${value.toFixed(1)}%` : `R$ ${formatCurrencyValue(value)}`
  const chartRef = useRef<ChartJS<'line'>>(null)

  let variation: number | null = null
  if (previousValue !== null && previousValue !== 0) {
    variation = ((value - previousValue) / Math.abs(previousValue)) * 100
  }

  const sparklineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: { display: false },
      y: { display: false }
    },
    elements: {
      point: { radius: 0 },
      line: { tension: 0.4, borderWidth: 1 }
    }
  }

  // Plugin que cria o gradiente no momento do render, quando o canvas já existe
  const gradientPlugin = useMemo(
    () => ({
      id: 'gradientFill',
      afterLayout(chart: ChartJS<'line'>) {
        const { ctx, chartArea } = chart
        if (!chartArea) return

        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        gradient.addColorStop(0, `${color}70`)
        gradient.addColorStop(1, `${color}00`)

        chart.data.datasets[0].backgroundColor = gradient
      }
    }),
    [color]
  )

  const sparklineChartData = {
    labels: sparklineData.map((_, i) => i.toString()),
    datasets: [
      {
        data: sparklineData,
        borderColor: color,
        backgroundColor: 'transparent',
        fill: true
      }
    ]
  }

  return (
    <SectionCard variant="tile" minW="200px" flex="1">
      <Flex align="center" gap={2} mb={1}>
        <Flex align="center" justify="center" w="28px" h="28px" borderRadius="full" bg={`${color}15`}>
          <i className={icon} style={{ color, fontSize: '14px' }} />
        </Flex>
        <Text fontSize="sm" fontWeight="600" color="gray.700">
          {title}
        </Text>
        <Text variant="caption">({indicator})</Text>
      </Flex>

      <Flex align="baseline" gap={2}>
        <Text fontSize="lg" fontWeight="bold" color={color}>
          {formattedValue}
        </Text>
        {variation !== null && (
          <Flex align="center" gap={0.5}>
            <Text fontSize="xs" fontWeight="600" color={variation >= 0 ? 'green.500' : 'red.500'}>
              {variation >= 0 ? '+' : ''}
              {variation.toFixed(2)}%
            </Text>
            <Text fontSize="xs" color={variation >= 0 ? 'green.500' : 'red.500'}>
              {variation >= 0 ? '↑' : '↓'}
            </Text>
          </Flex>
        )}
      </Flex>

      <Box h="40px" mt={2} position="relative">
        <Line ref={chartRef} data={sparklineChartData} options={sparklineOptions} plugins={[gradientPlugin]} />
      </Box>
    </SectionCard>
  )
}
